import { useEffect, useState } from "react";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import swal from "sweetalert";
import {
  LoadingSpinner,
  customOption,
  customSingleValue,
  isUrdu,
} from "../../../../constants/const";
import { GetLanguageString } from "../../../helper/Components";
import useForm from "../../../shared/useForm";
import { ApiFinance } from "../ApiFinance";

const subCategoryValues = {
  category: "",
  subCategoryTypeId: "",
  dim2CategoryTypeId: "",
  code: "",
};

const AddFinanceSubCategory = ({ setAddModal, setReload, reload }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [financeSubCategoryTypes, setFinanceSubCategoryTypes] = useState([]);
  const [selectedSubCategoryType, setSelectedSubCategoryType] = useState(null);
  const [selectedDim2CategoryType, setSelectedDim2CategoryType] = useState(null);
  const { errors, setErrors, values, handleInputChange } = useForm(
    subCategoryValues
  );
  const toggle = () => setAddModal(false);

  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleDrag = (e, ui) => {
    const { x, y } = position;
    setPosition({ x: x + ui.deltaX, y: y + ui.deltaY });
  };

  useEffect(() => {
    setLoading(true);
    ApiFinance()
      .GetFinanceSubCategoryTypes()
      .then((res) => {
        setFinanceSubCategoryTypes(
          res.data.map((item) => ({
            value: item.id,
            label: item.type,
          }))
        );
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        swal({
          title: err,
          text: err.response.data,
          icon: "error",
          buttons: "OK",
        });
        setLoading(false);
      });
  }, []);

  const validate = () => {
    let temp = {};
    temp.category = values.category ? "" : t("common_field_required_error");
    temp.subCategoryTypeId = selectedSubCategoryType
      ? ""
      : t("common_field_required_error");
    temp.dim2CategoryTypeId = selectedDim2CategoryType
      ? ""
      : t("common_field_required_error");
    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  const preparePayload = () => {
    const payload = { ...values };
    payload.code = payload.code ?? "";
    payload.subCategoryTypeId = selectedSubCategoryType.value;
    payload.dim2CategoryTypeId = selectedDim2CategoryType.value;
    return payload;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const payload = preparePayload();
      setLoading(true);
      ApiFinance()
        .AddFinanceSubCategory(payload)
        .then(() => {
          setReload(!reload);
          setLoading(false);
          toggle();
        })
        .catch((err) => {
          setLoading(false);
          swal({
            title: err.message || "Something went wrong",
            text: err.response?.data || "",
            icon: "error",
            buttons: "OK",
          });
          toggle();
          console.error(err);
        });
    }
  };

  return (
    <div>
      <Draggable position={position} onDrag={handleDrag} cancel=".no-drag">
        <Modal isOpen={true} className="inmodal" autoFocus={false}>
          <ModalHeader className="modal-title" tag="h4">
            {
              <GetLanguageString props="finance_tab_finance_account_categories_add" />
            }
          </ModalHeader>
          {!loading ? (
            <Form onSubmit={handleSubmit}>
              <ModalBody>
                <FormGroup>
                  <Label>
                    {<GetLanguageString props="finance_tab_category" />}
                  </Label>
                  <Input
                    autoFocus
                    type="text"
                    name="category"
                    className={
                      isUrdu(values.category) ? "ur no-drag" : "no-drag"
                    }
                    value={values.category}
                    onChange={handleInputChange}
                    {...(errors.category && { invalid: true })}
                  />
                  {errors.category && (
                    <FormFeedback>
                      <h4>{errors.category}</h4>
                    </FormFeedback>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label className="col-form-label">
                    {
                      <GetLanguageString props="finance_tab_finance_sub_category_select_account_type" />
                    }
                  </Label>
                  <Select
                    components={{
                      Option: customOption,
                      SingleValue: customSingleValue,
                    }}
                    value={selectedSubCategoryType}
                    options={financeSubCategoryTypes}
                    className="basic-single no-drag"
                    classNamePrefix="select"
                    onChange={(v) => {
                      setSelectedSubCategoryType(v);
                      if (!selectedDim2CategoryType) {
                        setSelectedDim2CategoryType(v);
                      }
                      setErrors({
                        ...errors,
                        subCategoryTypeId: "",
                        dim2CategoryTypeId: selectedDim2CategoryType || v ? "" : errors.dim2CategoryTypeId,
                      });
                    }}
                  />
                  {errors.subCategoryTypeId && (
                    <h4 className="text-error">{errors.subCategoryTypeId}</h4>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label className="col-form-label">
                    {
                      <GetLanguageString props="finance_tab_finance_sub_category_select_dim2_type" />
                    }
                  </Label>
                  <Select
                    components={{
                      Option: customOption,
                      SingleValue: customSingleValue,
                    }}
                    value={selectedDim2CategoryType}
                    options={financeSubCategoryTypes}
                    className="basic-single no-drag"
                    classNamePrefix="select"
                    onChange={(v) => {
                      setSelectedDim2CategoryType(v);
                      setErrors({
                        ...errors,
                        dim2CategoryTypeId: "",
                      });
                    }}
                  />
                  {errors.dim2CategoryTypeId && (
                    <h4 className="text-error">{errors.dim2CategoryTypeId}</h4>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label>
                    {<GetLanguageString props="settings_code" /> || "Code"}
                  </Label>
                  <Input
                    type="text"
                    name="code"
                    className={isUrdu(values.code) ? "ur no-drag" : "no-drag"}
                    value={values.code}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button color="white" onClick={toggle}>
                  {<GetLanguageString props="common_cancel" />}
                </Button>
                <Button color="primary" type="submit">
                  {<GetLanguageString props="common_save_changes" />}
                </Button>
              </ModalFooter>
            </Form>
          ) : (
            LoadingSpinner()
          )}
        </Modal>
      </Draggable>
    </div>
  );
};

export default AddFinanceSubCategory;
